// Metas: uma por empresa (Sync) + métrica + período (mês inicial..mês final, livre).
// O simulador só preenche esse cadastro: "Transformar em meta" gera metas dos TOTALIZADORES do DRE e
// dos indicadores, para cada período da quebra escolhida. Contas, subcontas e categorias têm meta
// criada uma a uma (cadastro manual ou "Criar meta" numa célula do DRE no simulador).

import { deriveIndicators, INDICATOR_DEFS, isIndicatorAvailable, sumBaseIndicators } from './indicators';
import { periodsFor, sumMonths, toMonthKey } from './periods';

export const METRIC_TYPE = Object.freeze({
  CATEGORIA: 'categoria',
  NO: 'no',
  TOTALIZADOR: 'totalizador',
  INDICADOR: 'indicador',
  BASICA: 'basica',
});

export const METRIC_TYPE_LABELS = {
  [METRIC_TYPE.CATEGORIA]: 'Categoria',
  [METRIC_TYPE.NO]: 'Conta do DRE',
  [METRIC_TYPE.TOTALIZADOR]: 'Totalizador do DRE',
  [METRIC_TYPE.INDICADOR]: 'Indicador',
  [METRIC_TYPE.BASICA]: 'Financeiro',
};

/** Métricas financeiras básicas — só no cadastro manual (as chaves batem com o backend). */
export const BASIC_METRICS = [
  { key: 'ENTRADAS_REALIZADAS', label: 'Entradas realizadas', format: 'currency' },
  { key: 'SAIDAS_REALIZADAS', label: 'Saídas realizadas', format: 'currency' },
  { key: 'VENDAS_TOTAL', label: 'Total de vendas', format: 'currency' },
];

/** Identidade de uma métrica (tipo + chave). */
export const metricId = (type, key) => `${type}:${key}`;

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Catálogo de métricas de uma empresa a partir do modelo (dataset preparado, com ou sem valores):
 * [{ id, type, key, label, format, parentId, depth, group }] — na ordem da árvore do DRE.
 */
export function buildMetricCatalog(model, { includeBasic = false } = {}) {
  const out = [];
  if (model) {
    INDICATOR_DEFS.filter((def) => isIndicatorAvailable(model, def)).forEach((def) => {
      out.push({ id: metricId(METRIC_TYPE.INDICADOR, def.key), type: METRIC_TYPE.INDICADOR, key: def.key, label: def.label, format: def.format, group: 'Indicadores' });
    });

    const depthOf = (id) => {
      let depth = 0;
      let current = model.nodeById.get(id)?.parentId;
      while (current && model.nodeById.has(current) && depth < 50) { depth += 1; current = model.nodeById.get(current).parentId; }
      return depth;
    };
    const seenCats = new Set();
    model.orderedNodeIds.forEach((nodeId) => {
      const node = model.nodeById.get(nodeId);
      const type = node.isTotalizador ? METRIC_TYPE.TOTALIZADOR : METRIC_TYPE.NO;
      out.push({ id: metricId(type, nodeId), type, key: nodeId, label: node.descricao, format: 'currency', parentId: node.parentId || null, depth: depthOf(nodeId), group: 'DRE' });
      node.directCats.forEach((cod) => {
        if (seenCats.has(cod)) return;
        seenCats.add(cod);
        out.push({
          id: metricId(METRIC_TYPE.CATEGORIA, cod),
          type: METRIC_TYPE.CATEGORIA,
          key: cod,
          label: model.categories.get(cod)?.descricao || cod,
          format: 'currency',
          parentId: nodeId,
          depth: depthOf(nodeId) + 1,
          group: 'DRE',
        });
      });
    });
    // Categorias fora da DRE: meta por categoria (o backend aceita as categorias do ERP); os nós da
    // seção (agrupadores do simulador) não têm meta.
    (model.outsideRootId ? model.nodeById.get(model.outsideRootId).allCats : []).forEach((cod) => {
      if (seenCats.has(cod)) return;
      seenCats.add(cod);
      out.push({
        id: metricId(METRIC_TYPE.CATEGORIA, cod),
        type: METRIC_TYPE.CATEGORIA,
        key: cod,
        label: model.categories.get(cod)?.descricao || cod,
        format: 'currency',
        depth: 0,
        group: 'Categorias',
      });
    });
  }
  if (includeBasic) {
    BASIC_METRICS.forEach((m) => out.push({ id: metricId(METRIC_TYPE.BASICA, m.key), type: METRIC_TYPE.BASICA, key: m.key, label: m.label, format: m.format, group: 'Financeiro' }));
  }
  return out;
}

/** Períodos (yyyy-MM..yyyy-MM) de um ano para a quebra Anual / Trimestral / Mensal. */
export const goalPeriodsFor = (granularity, planYear) => periodsFor(granularity, planYear).map((p) => ({
  label: p.label,
  months: p.months,
  periodStart: toMonthKey(planYear, p.months[0] + 1),
  periodEnd: toMonthKey(planYear, p.months[p.months.length - 1] + 1),
}));

/** Tipos de métrica que "Transformar em meta" gera em lote. */
export const SIMULATOR_BULK_TYPES = [METRIC_TYPE.TOTALIZADOR, METRIC_TYPE.INDICADOR];

/** Valor projetado de uma métrica do DRE/indicador num conjunto de meses. */
export const simulatedMetricValue = (simulation, metric, months) => {
  if (metric.type === METRIC_TYPE.INDICADOR) {
    return deriveIndicators(sumBaseIndicators(simulation.baseIndicatorSeries, months))[metric.key];
  }
  if (metric.type === METRIC_TYPE.CATEGORIA) return sumMonths(simulation.catValues.get(metric.key), months);
  return sumMonths(simulation.statement.get(metric.key), months);
};

/** Período (yyyy-MM..yyyy-MM) de um conjunto contíguo de meses (índices 0..11) do ano do plano. */
export const periodOfMonths = (planYear, months) => ({
  periodStart: toMonthKey(planYear, Math.min(...months) + 1),
  periodEnd: toMonthKey(planYear, Math.max(...months) + 1),
});

/**
 * Metas a partir da simulação: totalizadores do DRE e indicadores × período da quebra.
 * Indicadores percentuais são recalculados sobre a soma do período (não é média de meses).
 */
export function buildGoalsFromSimulation(model, simulation, { planYear, granularity, ownerUserId = null }) {
  const periods = goalPeriodsFor(granularity, planYear);
  const items = [];
  buildMetricCatalog(model)
    .filter((metric) => SIMULATOR_BULK_TYPES.includes(metric.type))
    .forEach((metric) => {
      periods.forEach((p) => {
        items.push({
          metricType: metric.type,
          metricKey: metric.key,
          metricLabel: metric.label,
          format: metric.format,
          periodStart: p.periodStart,
          periodEnd: p.periodEnd,
          value: round2(simulatedMetricValue(simulation, metric, p.months)),
          ownerUserId,
        });
      });
    });
  return items;
}
